import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Modal, Space, notification } from 'antd';
import dayjs from 'dayjs';
import propertiesService from '../../services/propertiesService';
import userRoleUtils from '../../utils/userRoleUtils';

const weekdayOptions = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  .map((label, value) => ({ label, value }));

const errorMessage = error => {
  const data = error?.response?.data;
  return Object.values(data?.errors || {}).flat()[0] || data?.message || error?.message || 'Please try again.';
};

const PropertyRecurrencePanel = ({ propertyId, arrivalTime = '08:00', completionTime = '09:30' }) => {
  const allowed = userRoleUtils.canManagePropertyRecurrence();
  const [range, setRange] = useState([dayjs().add(1, 'day'), dayjs().add(29, 'day')]);
  const [weekdays, setWeekdays] = useState([dayjs().day()]);
  const [arrival, setArrival] = useState(arrivalTime.slice(0, 5));
  const [completion, setCompletion] = useState(completionTime.slice(0, 5));
  const [blockedDates, setBlockedDates] = useState([]);
  const [availabilityReady, setAvailabilityReady] = useState(false);
  const [preview, setPreview] = useState(null);
  const [requestKey, setRequestKey] = useState(null);
  const [result, setResult] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availabilityVersion, setAvailabilityVersion] = useState(0);

  useEffect(() => {
    setArrival(arrivalTime.slice(0, 5));
    setCompletion(completionTime.slice(0, 5));
  }, [arrivalTime, completionTime]);

  useEffect(() => {
    let active = true;
    setPreview(null);
    setRequestKey(null);
    setAvailabilityReady(false);
    if (!allowed || !propertyId) return () => { active = false; };
    propertiesService.getRecurrenceAvailability(propertyId, {
      from: dayjs().format('YYYY-MM-DD'), to: dayjs().add(365, 'day').format('YYYY-MM-DD'),
      arrival_time: arrival, completion_time: completion,
    }).then(response => {
      if (!active) return;
      setBlockedDates(response.blocked_dates || []);
      setAvailabilityReady(true);
      setError(null);
    }).catch(failure => {
      if (active) setError(errorMessage(failure));
    });
    return () => { active = false; };
  }, [allowed, propertyId, arrival, completion, availabilityVersion]);

  const parameters = () => ({
    from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD'),
    weekdays: [...weekdays].sort(), arrival_time: arrival, completion_time: completion,
  });

  const unavailableDate = date => date.isBefore(dayjs(), 'day') || date.isAfter(dayjs().add(365, 'day'), 'day') || blockedDates.includes(date.format('YYYY-MM-DD'));

  const previewOrders = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await propertiesService.previewRecurrence(propertyId, parameters());
      setPreview(response);
      setRequestKey(window.crypto.randomUUID());
    } catch (failure) {
      setPreview(null);
      setError(errorMessage(failure));
    } finally {
      setLoading(false);
    }
  };

  const submitBatch = async request => {
    setLoading(true);
    try {
      const response = await propertiesService.createRecurrence(propertyId, request);
      setResult(response);
      setPendingRequest(response.complete ? null : request);
      setPreview(null);
      setError(null);
      setAvailabilityVersion(version => version + 1);
      const message = response.complete
        ? `Created ${response.order_ids.length} recurring orders`
        : `Created ${response.order_ids.length} of ${response.requested_dates.length} recurring orders`;
      notification[response.complete ? 'success' : 'warning']({ message, description: response.error || undefined });
    } catch (failure) {
      setError(errorMessage(failure));
      setPendingRequest(request);
      setResult(current => ({
        ...(current || { complete: false, batch_id: null, requested_dates: request.dates, dates: [], pending_dates: request.dates, order_ids: [], checkout_links: [] }),
        confirmation_pending: true, error: errorMessage(failure),
      }));
      notification.error({ message: 'Could not create recurring orders', description: errorMessage(failure) });
    } finally {
      setLoading(false);
    }
  };

  const createOrders = () => {
    const request = { ...parameters(), dates: preview.dates, idempotency_key: requestKey };
    Modal.confirm({
      title: `Create ${preview.dates.length} recurring orders?`,
      content: 'Each date uses the saved default order items, current client prices and normal payment settings. Unavailable dates are skipped.',
      okText: 'Create recurring orders', cancelText: 'Cancel',
      onOk: () => submitBatch(request),
    });
  };

  const retryRemaining = () => Modal.confirm({
    title: `Retry ${result.pending_dates.length} remaining dates?`,
    content: 'Created orders remain in place. The remaining dates use this batch\'s confirmed items, prices and payment settings.',
    okText: 'Retry remaining dates', cancelText: 'Cancel',
    onOk: () => submitBatch(pendingRequest),
  });

  if (!allowed || !propertyId) return null;

  return (
    <section className="mt-6 rounded-lg border bg-white p-6" aria-label="Recurring property orders">
      <h3 className="mb-2 text-xl font-semibold">Recurring orders</h3>
      <p className="mb-4 text-sm text-gray-600">Repeat weekly on selected days until the end date. The calendar shows property availability for this time slot; occupied dates are grey and cannot be selected. Save changes to the property's default order before creating recurring orders.</p>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <label className="mb-1 block text-sm font-medium">Date range</label>
          <Space wrap>
            <DatePicker value={range[0]} placeholder="Start date" allowClear={false} format="DD MMM YYYY"
              disabled={!availabilityReady || loading || Boolean(pendingRequest)} disabledDate={unavailableDate}
              onChange={value => { if (!value) return; setRange([value, range[1].isBefore(value, 'day') ? value : range[1]]); setPreview(null); setRequestKey(null); }} />
            <span>to</span>
            <DatePicker value={range[1]} placeholder="End date" allowClear={false} format="DD MMM YYYY"
              disabled={!availabilityReady || loading || Boolean(pendingRequest)} disabledDate={date => unavailableDate(date) || date.isBefore(range[0], 'day')}
              onChange={value => { if (!value) return; setRange([range[0], value]); setPreview(null); setRequestKey(null); }} />
          </Space>
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="text-sm font-medium">Arrival time
            <input type="time" aria-label="Recurring arrival time" value={arrival} disabled={loading || Boolean(pendingRequest)}
              onChange={event => setArrival(event.target.value)} className="ml-2 rounded border p-2" />
          </label>
          <label className="text-sm font-medium">Completion time
            <input type="time" aria-label="Recurring completion time" value={completion} disabled={loading || Boolean(pendingRequest)}
              onChange={event => setCompletion(event.target.value)} className="ml-2 rounded border p-2" />
          </label>
        </div>
        <Checkbox.Group options={weekdayOptions} value={weekdays} disabled={loading || Boolean(pendingRequest)} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          onChange={value => { setWeekdays(value); setPreview(null); setRequestKey(null); }} />
        {error && <Alert type="error" showIcon message={error} />}
        <Button onClick={previewOrders} loading={loading} disabled={!availabilityReady || !range?.[0] || !range?.[1] || weekdays.length === 0 || Boolean(pendingRequest)}>Preview recurring orders</Button>
        {preview && <div>
          <p className="font-medium">{preview.dates.length} available dates</p>
          <p className="mt-1 text-sm">{preview.dates.map(date => dayjs(date).format('DD MMM YYYY')).join(', ') || 'No available dates in this range.'}</p>
          {preview.unavailable_dates.length > 0 && <p className="mt-2 text-sm text-amber-700">Skipped occupied dates: {preview.unavailable_dates.map(date => dayjs(date).format('DD MMM YYYY')).join(', ')}</p>}
          <p className="mt-2 text-sm text-gray-600">{preview.sync_enabled ? 'Sync to Schedox is enabled for this property.' : 'Sync to Schedox is off. Enable it on the user and property to sync these orders.'}</p>
          <Button type="primary" className="mt-3" onClick={createOrders} disabled={!preview.dates.length || loading}>Create recurring orders</Button>
        </div>}
        {result && <div>
          <Alert type={result.complete ? 'success' : 'warning'} showIcon
            message={result.confirmation_pending ? 'Confirmation interrupted. Retry the confirmed dates to retrieve or finish this batch.' : result.complete ? `Created ${result.order_ids.length} recurring orders` : `Created ${result.order_ids.length} of ${result.requested_dates.length} recurring orders`}
            description={result.complete ? undefined : result.error} />
          <p className="mt-2 text-sm">{result.dates.map((date, index) => `${dayjs(date).format('DD MMM YYYY')}: order #${result.order_ids[index]}`).join(' · ')}</p>
          {(result.checkout_links || []).map(link => <p key={link.date} className="mt-2 text-sm"><a href={link.url} target="_blank" rel="noopener noreferrer">Continue to payment for {dayjs(link.date).format('DD MMM YYYY')}</a></p>)}
          {pendingRequest && <div className="mt-3">
            <p className="mb-2 text-sm">Remaining dates: {result.pending_dates.map(date => dayjs(date).format('DD MMM YYYY')).join(', ')}</p>
            <Space wrap>
              <Button type="primary" onClick={retryRemaining} disabled={loading}>Retry remaining dates</Button>
              <Button onClick={() => setPendingRequest(null)} disabled={loading}>Review different dates</Button>
            </Space>
          </div>}
        </div>}
      </Space>
    </section>
  );
};

export default PropertyRecurrencePanel;
